import { Fragment, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  BackButton,
  BodyCell,
  Content,
  HeaderCell,
  Inner,
  QuestionGrid,
  QuestionLoaderCell,
  Score,
  StatusPill,
  SummaryGrid,
  SummaryLabelCell,
  SummaryValueCell,
  TableWrap,
  Tables,
  Title,
  TitleRow
} from './NtaResult.styles';
import useNtaResultData from './useNtaResultData';

const SUMMARY_ROWS = [
  [
    { type: 'label', text: 'Total Questions', key: 'totalQuestions-label' },
    { type: 'value', key: 'totalQuestions' },
    { type: 'label', text: 'Total Attempted', key: 'totalAttempted-label' },
    { type: 'value', key: 'totalAttempted' }
  ],
  [
    { type: 'label', text: 'Correct Answers', key: 'correctAnswers-label' },
    { type: 'value', key: 'correctAnswers' },
    { type: 'label', text: 'Incorrect Answers', key: 'incorrectAnswers-label' },
    { type: 'value', key: 'incorrectAnswers' }
  ]
];

const QUESTION_COLUMNS = [
  { key: 'label', header: 'Question No.' },
  { key: 'selectedOptions', header: 'Selected Options' },
  { key: 'status', header: 'Status' },
  { key: 'correctOption', header: 'Correct Option' }
];

const NtaResultPage = ({ onBack }) => {
  const scrollRef = useRef(null);
  const summary = useNtaResultData({ enabled: true });
  const {
    score,
    questionRows,
    loadMoreSolutions,
    hasMoreSolutions,
    isFetchingSolutions
  } = summary;

  const handleLoadMore = useCallback(() => {
    if (!hasMoreSolutions || isFetchingSolutions) {
      return Promise.resolve();
    }

    return loadMoreSolutions();
  }, [hasMoreSolutions, isFetchingSolutions, loadMoreSolutions]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return undefined;
    }

    const handleScroll = () => {
      const { scrollHeight, clientHeight, scrollTop } = scrollElement;
      if (scrollHeight - 350 <= clientHeight + scrollTop) {
        handleLoadMore();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [handleLoadMore]);

  const showTableLoader = hasMoreSolutions && isFetchingSolutions;
  const loadedRowCount = questionRows.filter((row) => !row.isPending).length;

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !hasMoreSolutions || isFetchingSolutions) {
      return;
    }

    const isScrollable =
      scrollElement.scrollHeight > scrollElement.clientHeight + 50;

    if (!isScrollable) {
      handleLoadMore();
    }
  }, [
    handleLoadMore,
    hasMoreSolutions,
    isFetchingSolutions,
    loadedRowCount,
    questionRows.length
  ]);

  const renderQuestionCell = (row, column, isLastRow) => {
    if (column.key === 'status') {
      return (
        <BodyCell
          key={`${row.key}-${column.key}`}
          role="cell"
          $hideBottomBorder={isLastRow}
        >
          <StatusPill $type={row.status.type}>{row.status.label}</StatusPill>
        </BodyCell>
      );
    }

    return (
      <BodyCell
        key={`${row.key}-${column.key}`}
        role="cell"
        $hideBottomBorder={isLastRow}
      >
        {row[column.key]}
      </BodyCell>
    );
  };

  return (
    <Content ref={scrollRef}>
      <Inner>
        <TitleRow>
          <Title>Result</Title>
          <BackButton type="button" onClick={onBack}>
            BACK
          </BackButton>
        </TitleRow>
        <Score>Score: {score}</Score>
        <Tables>
          <TableWrap>
            <SummaryGrid>
              {SUMMARY_ROWS.flat().map((cell) =>
                cell.type === 'label' ? (
                  <SummaryLabelCell key={cell.key}>
                    {cell.text}
                  </SummaryLabelCell>
                ) : (
                  <SummaryValueCell key={cell.key}>
                    {summary[cell.key]}
                  </SummaryValueCell>
                )
              )}
            </SummaryGrid>
          </TableWrap>
          <TableWrap>
            <QuestionGrid role="table" aria-label="Question-wise result">
              {QUESTION_COLUMNS.map((column) => (
                <HeaderCell key={column.key} role="columnheader">
                  {column.header}
                </HeaderCell>
              ))}
              {questionRows.map((row, index) => {
                const isLastRow =
                  index === questionRows.length - 1 && !showTableLoader;

                return (
                  <Fragment key={row.key}>
                    {QUESTION_COLUMNS.map((column) =>
                      renderQuestionCell(row, column, isLastRow)
                    )}
                  </Fragment>
                );
              })}
              {showTableLoader ? (
                <QuestionLoaderCell role="cell">
                  Loading more results...
                </QuestionLoaderCell>
              ) : null}
            </QuestionGrid>
          </TableWrap>
        </Tables>
      </Inner>
    </Content>
  );
};

NtaResultPage.propTypes = {
  onBack: PropTypes.func.isRequired
};

export default NtaResultPage;
